import React, { useCallback, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CMSubSection, CMField, CMTable, CMSection } from '../add-edit.types';
import SectionFieldToolbar from '../section-field-toolbar';
import SectionFieldIcons from '../section-field-icons';
import {
  FieldList,
  DropdownConfig,
  CheckboxConfig,
  RadioConfig,
  TextBoxConfig,
  DatePickerConfig,
  CommentBoxConfig,
  StatusConfig,
  TableColumnsEditor,
  SourceConfig,
  CalculationConfig,
  ImageConfig,
  CanvasConfig,
} from '../../../../../Components/Controls/cm-control-config';
import type {
  FieldItem,
  DropdownConfigValue,
  CheckboxConfigValue,
  RadioConfigValue,
  TextBoxConfigValue,
  DatePickerConfigValue,
  CommentBoxConfigValue,
  StatusConfigValue,
  CalculationConfigValue,
  ImageConfigValue,
  CanvasConfigValue,
} from '../../../../../Components/Controls/cm-control-config';
import type { SourceConfigValue } from '../../../../../Components/Controls/cm-control-config/types';
import type { SubSectionConfigBodyProps } from './sub-section-config-body.types';
import { DeleteIcon } from '../../../../../bombiix-icon';
import { NewInput } from '../../../../../Components/PIM/common/NewInput';
import { isFullWidthFieldType, isWidthTotalOverLimit } from '../utils/system-canvas-cm.utils';
import LibraryTitleField from '../../../../../Components/Controls/library-title-field';
import PanelFlagsRow from './panel-flags-row';
import FieldSetupCard from './field-setup-card';
import { getFieldSetupAccess } from './field-setup-access';
import { TimePickerConfig } from '../../../../../Components/Controls/cm-control-config';
import type { TimePickerConfigValue } from '../../../../../Components/Controls/cm-control-config';
import TitleVisibilityButton from '../../../../../Components/Controls/title-visibility-button';

function parseWidthPct(value: string | undefined): number {
  if (!value || typeof value !== 'string') return 0;
  const trimmed = value.trim();
  const match = /^(\d+(?:\.\d+)?)\s*%?$/.exec(trimmed);
  return match ? Number(match[1]) : 0;
}

const SubSectionConfigBody: React.FC<SubSectionConfigBodyProps> = ({
  subsection,
  onUpdate,
  onDelete,
  editingFieldPath,
  setEditingFieldPath,
  editingTablePath,
  setEditingTablePath,
  toFieldItem,
  applyConfigToField,
  showCommentBtn = true,
  subIndex = 0,
  showLibraryTitle = true,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const [widthError, setWidthError] = useState<string | null>(null);

  const fields = (subsection.content as Array<CMField | CMTable>).filter((contentItem) => contentItem && !('columns' in (contentItem as CMTable))) as CMField[];
  const items: FieldItem[] = fields
    .map((field: CMField, fieldArrayIndex: number) => {
      return toFieldItem(field, fieldArrayIndex);
    })
    .filter(Boolean) as FieldItem[];

  const forceDisableAllTools = React.useMemo(() => {
    let disabled = false;
    if (editingTablePath && editingTablePath.subIndex === subIndex) {
      disabled = true;
    } else if (editingFieldPath && editingFieldPath.subIndex === subIndex) {
      const fld = (subsection.content as Array<CMField | CMTable>)?.[editingFieldPath.fieldIndex];
      if (fld && !('columns' in (fld as CMTable)) && (fld as CMField).type === 'comment') {
        disabled = true;
      }
    }
    return disabled;
  }, [editingTablePath, editingFieldPath, subIndex, subsection.content]);

  const handleListChange = useCallback(
    (nextItems: FieldItem[]) => {
      const currentContent = subsection.content as Array<CMField | CMTable>;
      const fieldsOnly = currentContent.filter((contentItem) => contentItem && !('columns' in (contentItem as CMTable))) as CMField[];
      const tablesOnly = currentContent.filter((contentItem) => (contentItem as CMTable) && 'columns' in (contentItem as CMTable)) as CMTable[];

      const reorderedFields = nextItems
        .map((item) => {
          const originalIndex = Number(item.id.replace('f_', ''));
          const originalField = fieldsOnly[originalIndex];
          if (!originalField) return null;
          return { ...originalField, isAccordion: !!item.convertToAccordion } as CMField;
        })
        .filter((field): field is CMField => field !== null);

      const updatedContent: Array<CMField | CMTable> = [...reorderedFields, ...tablesOnly];
      onUpdate({ ...subsection, content: updatedContent });
    },
    [subsection, onUpdate],
  );

  const onEdit = useCallback(
    (item: FieldItem | null) => (item ? setEditingFieldPath({ subIndex, fieldIndex: Number(item.id.replace('f_', '')) }) : setEditingFieldPath(null)),
    [subIndex, setEditingFieldPath],
  );

  const wrapperSection: CMSection = React.useMemo(
    () => ({
      label: 'Section',
      title: '',
      isVisibleToSupplier: true,
      content: [subsection],
    }),
    [subsection],
  );

  const setToolbarSections = useCallback(
    (updater: React.SetStateAction<CMSection[]>) => {
      if (typeof updater === 'function') {
        const updated = updater([wrapperSection]);
        if (updated && updated.length > 0 && updated[0] && updated[0].content && updated[0].content.length > 0) {
          onUpdate(updated[0].content[0] as CMSubSection);
        }
      } else {
        if (updater && updater.length > 0 && updater[0] && updater[0].content && updater[0].content.length > 0) {
          onUpdate(updater[0].content[0] as CMSubSection);
        }
      }
    },
    [wrapperSection, onUpdate],
  );

  const handleLibrarySelect = (pulled: unknown) => {
    const fetchedSubSection = pulled as CMSubSection | undefined;
    if (fetchedSubSection) {
      onUpdate({ ...fetchedSubSection });
    }
  };

  const closeFieldEditor = () => setEditingFieldPath(null);

  const renderFieldEditor = (fieldIndex: number) => {
    if (!editingFieldPath || editingFieldPath.subIndex !== subIndex) return null;
    const field = (subsection.content as Array<CMField | CMTable>)[fieldIndex];
    if (!field || 'columns' in (field as CMTable)) return null;
    const item = toFieldItem(field as CMField, fieldIndex);
    if (!item) return null;
    const apply = (nextCfg: unknown) => {
      const updatedFields = (subsection.content as Array<CMField | CMTable>).map((contentItem, contentIndex) => {
        if (!((contentItem as CMField) && 'type' in (contentItem as CMField))) return contentItem;
        if (contentIndex !== fieldIndex) return contentItem;
        return applyConfigToField(contentItem as CMField, nextCfg);
      });
      onUpdate({ ...subsection, content: updatedFields });
    };
    const totalOtherPct = fields.reduce((sum, siblingField, siblingIndex) => (siblingIndex === fieldIndex || isFullWidthFieldType(siblingField.type) ? sum : sum + parseWidthPct(siblingField.width)), 0);
    const fieldCast = field as CMField;
    const currentPct = parseWidthPct(fieldCast.width);
    const isInvalidTotal = currentPct > 0 && isWidthTotalOverLimit(totalOtherPct + currentPct);
    const handleWidthChange = (nextWidth: string) => {
      const newPct = parseWidthPct(nextWidth || '');
      if (newPct > 0 && isWidthTotalOverLimit(totalOtherPct + newPct)) {
        setWidthError(WIDTH_OVER_LIMIT_MESSAGE);
        return;
      }
      setWidthError(null);
      apply({ ...(item.config as Record<string, unknown>), width: nextWidth || '' });
    };
    const shownWidthError = isInvalidTotal || widthError ? widthError || WIDTH_OVER_LIMIT_MESSAGE : null;
    return (
      <FieldSetupCard
        fieldType={item.type}
        title={(item.config as { title?: string }).title ?? ''}
        onTitleChange={(title) => apply({ ...(item.config as Record<string, unknown>), title })}
        onClose={closeFieldEditor}
        {...(!isFullWidthFieldType(item.type) ? { width: fieldCast.width ?? '', onWidthChange: handleWidthChange, widthError: shownWidthError } : {})}
        access={getFieldSetupAccess(fieldCast, item)}
        onAccessChange={(patch) => apply({ ...(item.config as Record<string, unknown>), ...patch })}
      >
        {renderFieldConfig(item, apply)}
      </FieldSetupCard>
    );
  };

  const renderFieldConfig = (item: FieldItem, apply: (nextCfg: unknown) => void) => {
    switch (item.type) {
      case 'dropdown':
        return <DropdownConfig value={item.config as DropdownConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'checkbox':
        return <CheckboxConfig value={item.config as CheckboxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'radio':
        return <RadioConfig value={item.config as RadioConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'text':
        return <TextBoxConfig value={item.config as TextBoxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'date':
        return <DatePickerConfig value={item.config as DatePickerConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'comment':
        return <CommentBoxConfig value={item.config as CommentBoxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'status':
        return <StatusConfig value={item.config as StatusConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'source':
        return <SourceConfig value={item.config as SourceConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'calculation':
        return <CalculationConfig value={item.config as CalculationConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'image':
        return <ImageConfig value={item.config as ImageConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'canvas':
        return <CanvasConfig value={item.config as CanvasConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
      case 'time':
        return <TimePickerConfig value={item.config as TimePickerConfigValue} onChange={apply} />;
      default:
        return null;
    }
  };

  /**
   * Replaces one field's configuration with an entry pulled from the control library.
   * @param fieldIndex - Index of the field within the sub-section's content.
   * @param config - The library entry's saved configuration.
   */
  const pullFieldConfig = (fieldIndex: number, config: unknown) => {
    const updatedFields = (subsection.content as Array<CMField | CMTable>).map((contentItem, contentIndex) => {
      if (contentIndex !== fieldIndex || !((contentItem as CMField) && 'type' in (contentItem as CMField))) return contentItem;
      return applyConfigToField(contentItem as CMField, config);
    });
    onUpdate({ ...subsection, content: updatedFields });
  };

  return (
    <>
      <div className="cm-panel-block">
        <PanelFlagsRow
          idPrefix={`sub-section-${subIndex}`}
          isAccordion={!!subsection.isAccordion}
          onAccordionChange={(checked) => onUpdate({ ...subsection, isAccordion: checked })}
          isVisibleToSupplier={subsection.isVisibleToSupplier !== false}
          onVisibleToSupplierChange={(checked) => onUpdate({ ...subsection, isVisibleToSupplier: checked })}
          trailing={
            onDelete ? (
              <button type="button" className="cm-panel-flags__delete" aria-label={t('COMMON.DELETE')} onClick={onDelete}>
                <DeleteIcon size={PANEL_ICON_SIZE} />
              </button>
            ) : null
          }
        />
        {showLibraryTitle ? (
          <LibraryTitleField
            value={subsection.title ?? ''}
            onChange={(value: string) => onUpdate({ ...subsection, title: value })}
            onPull={handleLibrarySelect}
            controlType="subSection"
            content={subsection}
            placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SUB_SECTION_TITLE')}
            isTitleShown={subsection.showSubSectionTitle !== false}
            onTitleShownChange={(isShown) => onUpdate({ ...subsection, showSubSectionTitle: isShown })}
          />
        ) : (
          <NewInput
            placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SUB_SECTION_TITLE')}
            value={subsection.title ?? ''}
            onChange={(e) => onUpdate({ ...subsection, title: e.target.value })}
            endAdornment={
              <TitleVisibilityButton
                isShown={subsection.showSubSectionTitle !== false}
                onChange={(isShown) => onUpdate({ ...subsection, showSubSectionTitle: isShown })}
              />
            }
          />
        )}
      </div>
      <SectionFieldIcons
        mode="subsection"
        subSection={subsection}
        onDelete={(itemIndex: number) => {
          const inner: Array<CMField | CMTable> = Array.isArray(subsection.content) ? [...subsection.content] : [];
          const filtered = inner.filter((_, idx: number) => idx !== itemIndex);
          onUpdate({ ...subsection, content: filtered });
        }}
      />
      <SectionFieldToolbar
        sections={[wrapperSection]}
        activeSectionIndex={0}
        setSections={setToolbarSections}
        forceDisableAll={forceDisableAllTools}
        targetSubIndex={subIndex}
        showCommentBtn={showCommentBtn}
      />
      {(() => {
        const tableIndex = (subsection.content as Array<CMField | CMTable>).findIndex(
          (contentItem) => (contentItem as CMTable) && 'columns' in (contentItem as CMTable),
        );
        if (tableIndex === -1) return null;
        const table = (subsection.content as Array<CMField | CMTable>)[tableIndex] as CMTable;
        const setTable = (updater: (table: CMTable) => CMTable) => {
          const inner: Array<CMField | CMTable> = Array.isArray(subsection.content) ? [...subsection.content] : [];
          const tableIndexInContent = inner.findIndex((contentItem) => 'columns' in (contentItem as CMTable));
          if (tableIndexInContent === -1) return;
          const updatedTable = updater({ ...(inner[tableIndexInContent] as CMTable) });
          inner[tableIndexInContent] = updatedTable;
          onUpdate({ ...subsection, content: inner });
        };
        return (
          <TableColumnsEditor
            table={table}
            setTable={setTable}
            subIndex={subIndex}
            editingTablePath={editingTablePath}
            setEditingTablePath={setEditingTablePath}
            toFieldItem={toFieldItem}
            applyConfigToField={applyConfigToField}
            showLibraryTitle={showLibraryTitle}
          />
        );
      })()}
      {items.length > 0 && (
        <FieldList
          fields={items}
          onChange={handleListChange}
          onEdit={onEdit}
          {...(editingFieldPath?.subIndex === subIndex && editingFieldPath?.fieldIndex !== undefined && { selectedIndex: editingFieldPath.fieldIndex })}
          showLibraryButton={showLibraryTitle}
          onLibraryPull={pullFieldConfig}
          renderEditor={renderFieldEditor}
        />
      )}
    </>
  );
};

export default memo(SubSectionConfigBody);

const PANEL_ICON_SIZE = 20;

const WIDTH_OVER_LIMIT_MESSAGE = 'Total width must not exceed 100%';
